import { useEffect, useRef, useState } from "react";
import { api, fmtMoney, formatApiError, CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Gift, X } from "lucide-react";
import BestCardSuggest from "@/components/BestCardSuggest";

export default function QuickEntry() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [cards, setCards] = useState([]);
    const fileInputRef = useRef(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const [receiptPreview, setReceiptPreview] = useState(null);
    const [f, setF] = useState({
        date: new Date().toISOString().slice(0, 10),
        card_id: "",
        transaction_type: "Debit",
        merchant: "",
        amount: "",
        category: "Other",
        business_personal: "Business",
        remarks: "",
        cashback_received: false,
        cashback_amount: "",
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/cards").then((r) => {
            setCards(r.data);
            const preCard = searchParams.get("card");
            const initial = preCard && r.data.find((c) => c.id === preCard) ? preCard : r.data[0]?.id;
            if (initial) setF((s) => ({ ...s, card_id: initial }));
        });
    }, [searchParams]);

    // Fuzzy Command-Palette pre-fill: /quick-entry?amount=500&note=lunch&merchant=Zomato
    useEffect(() => {
        const amt = searchParams.get("amount");
        const note = searchParams.get("note") || searchParams.get("remarks");
        const merchant = searchParams.get("merchant");
        const category = searchParams.get("category");
        if (!amt && !note && !merchant && !category) return;
        setF((s) => ({
            ...s,
            amount: amt || s.amount,
            merchant: merchant || note || s.merchant,
            remarks: note || s.remarks,
            category: (category && CATEGORIES.includes(category)) ? category : s.category,
        }));
    }, [searchParams]);

    // Receipt-first mode: `/quick-entry?receipt=1` auto-opens the device's
    // camera / photo picker on mount so the user can snap the bill before
    // filling anything else. Used by the mobile QuickAdd FAB "Card expense"
    // tile. Falls back silently if the file input can't be triggered (some
    // desktop browsers block programmatic click without a user gesture —
    // the input is still visible for a manual click).
    useEffect(() => {
        if (searchParams.get("receipt") !== "1") return;
        const timer = setTimeout(() => {
            try { fileInputRef.current?.click(); } catch { /* noop */ }
        }, 350);
        return () => clearTimeout(timer);
    }, [searchParams]);
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const onReceiptPick = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Reject anything absurdly large — receipts are small photos/PDFs.
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Receipt too large (max 8 MB)");
            return;
        }
        setReceiptFile(file);
        if (file.type?.startsWith("image/")) {
            const url = URL.createObjectURL(file);
            setReceiptPreview(url);
        } else {
            setReceiptPreview(null);
        }
    };

    const clearReceipt = () => {
        setReceiptFile(null);
        if (receiptPreview) URL.revokeObjectURL(receiptPreview);
        setReceiptPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                ...f,
                amount: Number(f.amount || 0),
                cashback_amount: f.cashback_received ? Number(f.cashback_amount || 0) : 0,
            };
            const { data } = await api.post("/expenses", payload);
            // Upload receipt AFTER expense creation, if the user picked one
            if (receiptFile && data?.id) {
                try {
                    const form = new FormData();
                    form.append("file", receiptFile);
                    await api.post(`/expenses/${data.id}/receipt`, form, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                } catch (upErr) {
                    // The expense is saved — surface a soft warning, not an error
                    console.error("[QuickEntry] receipt upload failed", upErr);
                    toast.warning("Expense saved, but receipt upload failed. Try re-attaching from the expense list.");
                }
            }
            const saved = payload.cashback_received && payload.cashback_amount > 0;
            const baseMsg = saved ? `Expense saved · +${fmtMoney(payload.cashback_amount)} cashback logged` : "Expense recorded";
            toast.success(receiptFile ? `${baseMsg} · receipt attached` : baseMsg);
            if (data?._duplicate_warning) {
                toast.warning(data._duplicate_warning, { duration: 8000 });
            }
            if (data?._warning) {
                toast.warning(data._warning, { duration: 6000 });
            }
            setF({
                ...f,
                date: new Date().toISOString().slice(0, 10),
                transaction_type: "Debit",
                merchant: "", amount: "", remarks: "",
                cashback_received: false, cashback_amount: "",
            });
            clearReceipt();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="quick-entry-page" className="max-w-lg mx-auto">
            <div className="text-center mb-6">
                <div className="eyebrow">On the go</div>
                <h1 className="font-display text-4xl text-stone-900 leading-none mt-2">
                    Quick expense.
                </h1>
                <p className="text-sm text-stone-500 mt-2">Log a spend the moment it happens.</p>
            </div>
            <form onSubmit={submit} className="form-section space-y-4">
                <div>
                    <Label className="eyebrow">Date of expense</Label>
                    <Input
                        type="date"
                        required
                        value={f.date}
                        onChange={setInput("date")}
                        max={new Date().toISOString().slice(0, 10)}
                        className="mt-1.5 font-mono-figure"
                        data-testid="qe-date"
                    />
                    <div className="text-[11px] text-stone-500 mt-1">
                        Defaults to today — change if you&rsquo;re logging a past spend.
                    </div>
                </div>
                <div>
                    <Label className="eyebrow">Amount</Label>
                    <Input type="number" step="0.01" required inputMode="decimal" value={f.amount} onChange={setInput("amount")} className="mt-1.5 h-14 text-2xl font-mono-figure" data-testid="qe-amount" />
                </div>
                <div>
                    <Label className="eyebrow">Transaction type</Label>
                    <Select value={f.transaction_type} onValueChange={set("transaction_type")}>
                        <SelectTrigger className="mt-1.5" data-testid="qe-txn-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Debit">
                                <span className="inline-flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-red-500" />
                                    Debit — money going out (spend)
                                </span>
                            </SelectItem>
                            <SelectItem value="Credit">
                                <span className="inline-flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                    Credit — money coming in (refund / reversal)
                                </span>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="text-[11px] text-stone-500 mt-1">
                        Pick <strong>Credit</strong> for reversals, chargebacks or bank-credited cashback so it doesn&rsquo;t inflate your spend total.
                    </div>
                </div>
                <div>
                    <Label className="eyebrow">Card</Label>
                    <Select value={f.card_id} onValueChange={set("card_id")}>
                        <SelectTrigger className="mt-1.5" data-testid="qe-card"><SelectValue placeholder="Select card" /></SelectTrigger>
                        <SelectContent>
                            {cards.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Merchant</Label>
                    <Input required value={f.merchant} onChange={setInput("merchant")} className="mt-1.5" data-testid="qe-merchant" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="eyebrow">Category</Label>
                        <Select value={f.category} onValueChange={set("category")}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-64">
                                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="eyebrow">Business / Personal</Label>
                        <Select value={f.business_personal} onValueChange={set("business_personal")}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Business">Business</SelectItem>
                                <SelectItem value="Personal">Personal</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div>
                    <Label className="eyebrow">Remarks</Label>
                    <Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" />
                </div>

                {/* Cashback capture */}
                <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-emerald-800">
                            <Gift size={14} />
                            <span>Received cashback on this spend?</span>
                        </div>
                        <Switch
                            checked={f.cashback_received}
                            onCheckedChange={set("cashback_received")}
                            data-testid="qe-cashback-toggle"
                        />
                    </div>
                    {f.cashback_received && (
                        <div className="mt-3">
                            <Label className="eyebrow">Cashback amount</Label>
                            <Input
                                type="number"
                                step="0.01"
                                inputMode="decimal"
                                value={f.cashback_amount}
                                onChange={setInput("cashback_amount")}
                                className="mt-1.5 font-mono-figure"
                                placeholder="0.00"
                                data-testid="qe-cashback-amount"
                            />
                            <div className="text-[11px] text-emerald-700 mt-1.5">
                                Rolls up to your yearly cashback savings on the dashboard.
                            </div>
                        </div>
                    )}
                </div>

                {/* Receipt photo — mobile-first: uses camera capture on phones */}
                <div className="rounded-md border border-stone-200 bg-stone-50/60 p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm text-stone-700">
                            <Camera size={14} />
                            <span>Receipt photo <span className="text-stone-400">(optional)</span></span>
                        </div>
                        {receiptFile && (
                            <button
                                type="button"
                                onClick={clearReceipt}
                                className="text-[11px] text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
                                data-testid="qe-receipt-clear"
                            >
                                <X size={11} /> Remove
                            </button>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        capture="environment"
                        onChange={onReceiptPick}
                        className="block w-full text-sm text-stone-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-stone-900 file:text-white hover:file:bg-stone-700"
                        data-testid="qe-receipt-input"
                    />
                    {receiptFile && (
                        <div className="mt-2 flex items-center gap-3">
                            {receiptPreview ? (
                                <img
                                    src={receiptPreview}
                                    alt="Receipt preview"
                                    className="w-16 h-16 object-cover rounded-md border border-stone-200"
                                    data-testid="qe-receipt-preview"
                                />
                            ) : (
                                <div className="w-16 h-16 rounded-md border border-stone-200 bg-white flex items-center justify-center text-[11px] text-stone-500">
                                    PDF
                                </div>
                            )}
                            <div className="text-[12px] text-stone-600 truncate">{receiptFile.name}</div>
                        </div>
                    )}
                    <div className="text-[11px] text-stone-500 mt-1.5">
                        Attach a photo of the bill or an e-receipt PDF. Uploads happen right after you save.
                    </div>
                </div>

                <Button type="submit" disabled={busy} className="w-full h-12 bg-stone-900 hover:bg-stone-800 text-white" data-testid="qe-submit">
                    {busy ? "Saving…" : "Record expense"}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={() => nav("/expenses")} data-testid="qe-view-all">
                    View all expenses
                </Button>
            </form>
        </div>
    );
}
